import { useCallback, type ReactElement, type ReactNode } from 'react';

import { getArtifact } from '@/artifacts';
import { SlidePanel } from '@/components/ui/SlidePanel';
import { useSlidePanelVariant } from '@/hooks';
import { useArtifactNavigation } from '@/hooks/useArtifactNavigation';
import { useGeoMarkupStore } from '@/store';
import { PANEL_DEFAULT_WIDTH } from '@/constants/ui';

import { DetailOverlayProvider } from './DetailOverlayContext';
import {
  useDetailOverlayFullscreen,
  useDetailOverlaySlots,
} from './useDetailOverlay';
import { useActiveDetailArtifact } from './useActiveDetailArtifact';

interface UnifiedDetailOverlayProps {
  fullscreenTopBar?: ReactNode;
}

/**
 * Single SlidePanel shell shared by every artifact's detail view.
 *
 * The shell's `opened` prop is driven by the OR of all artifact detail
 * flags, so navigating directly between a note detail and a task detail
 * (e.g. clicking a linked task inside a note) swaps content in place
 * without triggering the panel's close+open animation.
 *
 * The active artifact is resolved from the URL first (authoritative when
 * present) and falls back to the store flags (transitional / programmatic
 * opens). Its `DetailContent` is rendered inside the SlidePanel body;
 * the content component publishes header + toolbar slots to this shell
 * via `useRegisterDetailSlots`.
 */
export function UnifiedDetailOverlay({
  fullscreenTopBar,
}: UnifiedDetailOverlayProps): ReactElement {
  return (
    <DetailOverlayProvider>
      <UnifiedDetailOverlayInner fullscreenTopBar={fullscreenTopBar} />
    </DetailOverlayProvider>
  );
}

function UnifiedDetailOverlayInner({
  fullscreenTopBar,
}: UnifiedDetailOverlayProps): ReactElement {
  const { activeArtifactId, isAnyDetailOpen } = useActiveDetailArtifact();
  const slots = useDetailOverlaySlots();
  const { setIsFullscreenMode } = useDetailOverlayFullscreen();
  const { closeOverlay } = useArtifactNavigation();
  const closeAllTabs = useGeoMarkupStore(
    (state) => state.taskNavigation.closeAllTabs
  );
  const slidePanelVariant = useSlidePanelVariant();

  const handleClose = useCallback(() => {
    closeOverlay();
    closeAllTabs();
  }, [closeOverlay, closeAllTabs]);

  const handleFullscreenChange = useCallback(
    (next: boolean) => setIsFullscreenMode(next),
    [setIsFullscreenMode]
  );

  const activeArtifact = activeArtifactId
    ? getArtifact(activeArtifactId)
    : null;
  const ActiveContent = activeArtifact?.DetailContent ?? null;

  return (
    <SlidePanel
      opened={isAnyDetailOpen}
      onClose={handleClose}
      variant={slidePanelVariant}
      width={PANEL_DEFAULT_WIDTH}
      headerContent={slots.headerContent}
      showFooter={false}
      previewMode={slots.previewMode ?? false}
      onPreviewModeChange={slots.onPreviewModeChange}
      onEdit={slots.onEdit}
      onDelete={slots.onDelete}
      fullscreenTopBar={fullscreenTopBar}
      defaultFullscreen={slots.defaultFullscreen ?? false}
      onFullscreenChange={handleFullscreenChange}
      fullscreenWideContent={slots.fullscreenWideContent ?? false}
    >
      {ActiveContent ? <ActiveContent /> : null}
    </SlidePanel>
  );
}
